import { useState, useEffect } from "react";
import { formatLocalDate } from "@/lib/utils/format-local-date";
import {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../../components/ui/dialog";
import { Dialog } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { Textarea } from "../../../components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../components/ui/select";
import { TextDateInputDelayedSubmission } from "../../../components/ui/text-date-input";
import { TableInvoiceRow, TablePaymentRow } from "../types/invoice-table.types";
import { PaymentType } from "@/dentlab/src/types/database-enums/payment-enums";

interface PaymentDialogProps {
	open: boolean;
	onClose: () => void;
	invoice: TableInvoiceRow | null;
	onSubmit: (paymentData: {
		invoiceId: number;
		amount: number;
		valutaDate: string;
		paymentType: PaymentType;
		note: string;
		generatePaymentSlip: boolean;
		bookRemainderAsLoss: boolean;
		lossComment: string;
	}) => Promise<void>;
	loading?: boolean;
}

export const PaymentDialog: React.FC<PaymentDialogProps> = ({
	open,
	onClose,
	invoice,
	onSubmit,
	loading = false,
}) => {
	const [amount, setAmount] = useState<string>("");
	const [valutaDate, setValutaDate] = useState<string>("");
	const [paymentType, setPaymentType] = useState<PaymentType>(
		PaymentType.BANKZAHLUNG
	);
	const [note, setNote] = useState<string>("");
	const [generatePaymentSlip, setGeneratePaymentSlip] =
		useState<boolean>(false);
	const [printReceipt, setPrintReceipt] = useState<boolean>(false);
	const [bookRemainderAsLoss, setBookRemainderAsLoss] =
		useState<boolean>(false);
	const [lossComment, setLossComment] = useState<string>("");

	// Initialize form with invoice data when dialog opens
	useEffect(() => {
		if (open && invoice) {
			// Calculate remaining amount due from total allocated payments
			const totalAllocatedToInvoice =
				(invoice.payments as unknown as TablePaymentRow[])?.reduce(
					(sum, payment) => {
						const paymentValid =
							payment.payment_id !== null &&
							payment.payment_canceled_at === null &&
							payment.allocation_canceled_at === null;
						return paymentValid
							? sum + (payment.allocated_amount || 0)
							: sum;
					},
					0
				) || 0;

			const remainingDue = Math.max(
				0,
				(invoice.invoice_value || 0) - totalAllocatedToInvoice
			);

			setAmount(remainingDue.toFixed(2));
			setValutaDate(formatLocalDate(new Date()));
			setPaymentType(PaymentType.BANKZAHLUNG); // Default to bank transfer
			setNote("");
			setGeneratePaymentSlip(false);
			setPrintReceipt(false);
			setBookRemainderAsLoss(false);
			setLossComment("");
		}
	}, [open, invoice]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!invoice) return;

		const numericAmount = parseFloat(amount);
		if (isNaN(numericAmount) || numericAmount <= 0) {
			alert("Bitte geben Sie einen gültigen Betrag ein.");
			return;
		}

		if (!valutaDate) {
			alert("Bitte wählen Sie ein Valutadatum.");
			return;
		}

		if (bookRemainderAsLoss && !lossComment.trim()) {
			alert("Bitte geben Sie einen Grund für die Verlustbuchung ein.");
			return;
		}

		await onSubmit({
			invoiceId: invoice.invoice_id || 0,
			amount: numericAmount,
			valutaDate,
			paymentType,
			note: note.trim(),
			generatePaymentSlip,
			bookRemainderAsLoss,
			lossComment: lossComment.trim(),
		});
	};

	const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		// Allow only numbers and decimal point
		if (/^\d*\.?\d*$/.test(value)) {
			setAmount(value);
		}
	};

	const paymentAmount = parseFloat(amount || "0");
	const invoiceValue = invoice?.invoice_value || 0;

	// Calculate actual remaining amount after existing payments (exclude canceled payments)
	const totalAllocatedToInvoice =
		(invoice?.payments as unknown as TablePaymentRow[])?.reduce(
			(sum, payment) => {
				const paymentValid =
					payment.payment_id !== null &&
					payment.payment_canceled_at === null &&
					payment.allocation_canceled_at === null;
				return paymentValid
					? sum + (payment.allocated_amount || 0)
					: sum;
			},
			0
		) || 0;

	// Round to 2 decimal places to avoid floating point precision issues
	const actualRemainingDue = Math.max(
		0,
		Math.round((invoiceValue - totalAllocatedToInvoice) * 100) / 100
	);
	const remainingAfterThisPayment = Math.max(
		0,
		Math.round((actualRemainingDue - paymentAmount) * 100) / 100
	);

	const isOverpayment = paymentAmount > actualRemainingDue;
	const overpaymentAmount = isOverpayment
		? Math.round((paymentAmount - actualRemainingDue) * 100) / 100
		: 0;

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Zahlung erfassen</DialogTitle>
					<DialogDescription>
						Erfassen Sie eine Zahlung für Rechnung{" "}
						<span className="font-mono font-semibold">
							{invoice?.invoice_doc_number}
						</span>
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="flex items-center gap-4">
						<Label htmlFor="amount" className="w-32 text-right">
							Betrag ({invoice?.invoice_currency})
						</Label>
						<Input
							id="amount"
							type="text"
							value={amount}
							onChange={handleAmountChange}
							placeholder="0.00"
							required
							disabled={loading}
							className="flex-1"
						/>
					</div>

					<div className="flex items-center gap-4">
						<Label
							htmlFor="valuta-date"
							className="w-32 text-right"
						>
							Valutadatum
						</Label>
						<div className="flex-1">
							<TextDateInputDelayedSubmission
								value={valutaDate}
								onDateChange={(value) =>
									setValutaDate(value || "")
								}
								disabled={loading}
								placeholder="TT.MM.JJJJ"
							/>
						</div>
					</div>

					<div className="flex items-center gap-4">
						<Label
							htmlFor="payment-type"
							className="w-32 text-right"
						>
							Zahlungsart
						</Label>
						<Select
							value={paymentType.toString()}
							onValueChange={(value) =>
								setPaymentType(parseInt(value) as PaymentType)
							}
						>
							<SelectTrigger className="flex-1">
								<SelectValue placeholder="Zahlungsart wählen">
									{paymentType === PaymentType.BARZAHLUNG &&
										"Bar"}
									{paymentType === PaymentType.BANKZAHLUNG &&
										"Bank"}
									{paymentType === PaymentType.TWINT &&
										"TWINT"}
									{paymentType === PaymentType.DEBITKARTE &&
										"Debitkarte"}
									{paymentType ===
										PaymentType.KARTENZAHLUNG &&
										"Kreditkarte"}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem
									value={PaymentType.BARZAHLUNG.toString()}
								>
									Bar
								</SelectItem>
								<SelectItem
									value={PaymentType.BANKZAHLUNG.toString()}
								>
									Bank
								</SelectItem>
								<SelectItem
									value={PaymentType.TWINT.toString()}
								>
									TWINT
								</SelectItem>
								<SelectItem
									value={PaymentType.DEBITKARTE.toString()}
								>
									Debitkarte
								</SelectItem>
								<SelectItem
									value={PaymentType.KARTENZAHLUNG.toString()}
								>
									Kreditkarte
								</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className="flex items-center gap-4">
						<Label htmlFor="note" className="w-32 text-right">
							Bemerkung (optional)
						</Label>
						<Input
							id="note"
							type="text"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Bemerkung zur Zahlung"
							disabled={loading}
							className="flex-1"
						/>
					</div>

					{/* Overpayment Warning */}
					{isOverpayment && (
						<div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
							<div className="flex items-start space-x-2">
								<div className="flex-shrink-0">
									<svg
										className="w-5 h-5 text-orange-600 mt-0.5"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="space-y-2">
									<p className="text-sm font-medium text-orange-800">
										Überzahlung - Aufteilung der Zahlung
									</p>
									<div className="text-sm text-orange-700 space-y-1">
										<div className="flex justify-between">
											<span>Zahlung an Rechnung:</span>
											<span className="font-mono">
												CHF{" "}
												{actualRemainingDue.toFixed(2)}
											</span>
										</div>
										{overpaymentAmount > 0.005 && (
											<div className="flex justify-between">
												<span>
													Akonto für künftige
													Rechnungen:
												</span>
												<span className="font-mono">
													CHF{" "}
													{overpaymentAmount.toFixed(
														2
													)}
												</span>
											</div>
										)}
									</div>
									<p className="text-xs text-orange-600">
										Der Überschussbetrag wird als Akonto
										gespeichert und automatisch für künftige
										Rechnungen verwendet.
									</p>
								</div>
							</div>
						</div>
					)}

					<div className="space-y-3">
						{/* <div className="flex items-center space-x-2">
							<Checkbox
								id="print-receipt"
								checked={printReceipt}
								onCheckedChange={(checked) =>
									setPrintReceipt(checked as boolean)
								}
								disabled={true} // Disabled for future implementation
							/>
							<Label htmlFor="print-receipt" className="text-sm font-medium text-gray-500">
								Quittung drucken (noch nicht implementiert)
							</Label>
						</div> */}

						{/* <div className="flex items-center space-x-2">
							<Checkbox
								id="payment-slip"
								checked={generatePaymentSlip}
								onCheckedChange={(checked) =>
									setGeneratePaymentSlip(checked as boolean)
								}
								disabled={loading || remainingAfterThisPayment <= 0 || bookRemainderAsLoss}
							/>
							<div className="space-y-1">
								<Label htmlFor="payment-slip" className={`text-sm font-medium ${remainingAfterThisPayment <= 0 || bookRemainderAsLoss ? 'text-gray-400' : ''}`}>
									Zahlschein für Restbetrag erstellen
								</Label>
								{remainingAfterThisPayment > 0 && !loading && !bookRemainderAsLoss && (
									<p className="text-xs text-gray-600">
										Restbetrag: CHF {remainingAfterThisPayment.toFixed(2)}
									</p>
								)}
							</div>
						</div> */}

						<div className="flex items-center space-x-2">
							<Checkbox
								id="book-remainder-as-loss"
								checked={bookRemainderAsLoss}
								onCheckedChange={(checked) => {
									const isChecked = checked as boolean;
									setBookRemainderAsLoss(isChecked);
									if (isChecked) {
										setGeneratePaymentSlip(false);
									}
								}}
								disabled={
									loading || remainingAfterThisPayment <= 0
								}
							/>
							<div className="space-y-1">
								<Label
									htmlFor="book-remainder-as-loss"
									className={`text-sm font-medium ${
										remainingAfterThisPayment <= 0
											? "text-gray-400"
											: ""
									}`}
								>
									Restbetrag als Verlust buchen
								</Label>
								{remainingAfterThisPayment > 0 &&
									!loading &&
									bookRemainderAsLoss && (
										<p className="text-xs text-red-600">
											Verlust: CHF{" "}
											{remainingAfterThisPayment.toFixed(
												2
											)}
										</p>
									)}
								{remainingAfterThisPayment <= 0 && (
									<p className="text-xs text-gray-500">
										Nur bei Teilzahlungen verfügbar
									</p>
								)}
							</div>
						</div>

						{bookRemainderAsLoss &&
							remainingAfterThisPayment > 0 && (
								<div className="space-y-2">
									<Label
										htmlFor="loss-comment"
										className="text-sm font-medium"
									>
										Grund für Verlustbuchung{" "}
										<span className="text-red-600">*</span>
									</Label>
									<Textarea
										id="loss-comment"
										value={lossComment}
										onChange={(e) =>
											setLossComment(e.target.value)
										}
										placeholder="Bitte geben Sie den Grund für die Verlustbuchung ein..."
										className="min-h-[80px]"
										disabled={loading}
										data-testid="loss-comment-input"
									/>
								</div>
							)}
					</div>
					<DialogFooter className="flex gap-2 justify-end">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={loading}
						>
							Abbrechen
						</Button>
						<Button type="submit" disabled={loading}>
							{loading
								? "Wird gespeichert..."
								: "Zahlung erfassen"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
